import { useContext, useMemo, useState } from 'react';

import { TerminalContext } from '../../../TerminalInit';
import { useSpotLayout } from '../../../api/spotLayouts';
import { useSpot } from '../../../api/spots';
import { useTransactions } from '../../../api/transactions';
import { Transaction, TransactionStatus } from '../../../common/transactions';
import { checkIfTransactionInProgressIsStillValid } from '../../../service-worker/TransactionInProgressDatabase';
import { arraysEqual } from '../../../services/global/arrays';
import { asyncTransactionFilter } from '../../../services/transaction/TransactionFilters';
import useBaseLookup from '../useBaseLookup';

interface LookupDropoffQrProps {
    lookupValue: string | null;
}

const findTransactionsByDropoffQr = (transactions: Transaction[], qr: string): Promise<Transaction[]> => {
    // For all customers right now, tt_number is the dropoff QR.
    return asyncTransactionFilter(transactions, async (transaction: Transaction) => {
        if (transaction.tt_number === qr) {
            if (transaction.status === TransactionStatus.DROPOFF_IN_PROGRESS) {
                await checkIfTransactionInProgressIsStillValid(transaction);
                return true;
            } else {
                return transaction.status === TransactionStatus.READY_FOR_DROPOFF;
            }
        }
        return;
    });
};

const useLookupDropoffQr = (props: LookupDropoffQrProps) => {
    const { terminal } = useContext(TerminalContext);
    const enabled = props.lookupValue != null;
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url, undefined, { enabled });
    const { data: spot } = useSpot(spotLayout?.spot_url, undefined, { enabled: enabled && !!spotLayout?.spot_url });
    const [result, changeResult] = useState<Transaction[] | undefined>(undefined);

    const spotTransactionsResult = useTransactions({ spot: spot?.id }, { enabled: enabled && !!spot?.id });
    const { data: transactions, refetch: refetchTransactions, isFetching: transactionsIsFetching } = spotTransactionsResult;

    const requiredDataFetched = !!transactions && (spotTransactionsResult.isSuccess || spotTransactionsResult.isError);

    useMemo(async (): Promise<Transaction[]> => {
        if (!props.lookupValue || !transactions || !requiredDataFetched) return [];
        return await findTransactionsByDropoffQr(transactions, props.lookupValue);
    }, [props.lookupValue, transactions, requiredDataFetched]).then((tr) => {
        if (tr && (result === undefined || !arraysEqual(tr, result))) {
            changeResult(tr);
        }
    });

    const LookupImpl = {
        lookupValue: props.lookupValue,
        requiredDataFetched,
        result: result,
        refetchTransactions,
        isRefetching: transactionsIsFetching,
    };

    return useBaseLookup(LookupImpl);
};

export default useLookupDropoffQr;
